import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  asSpelled,
  CompactBar,
  CompactChipLine,
  CompactDivider,
  CompactFilters,
  CompactMenu,
  CompactPortal,
  CompactSearch,
  CompactTitle,
  DataTable,
  HelpPopover,
  NotPermitted,
  PageHeader,
  ReasonDialog,
  SelectChip,
  STICKY_BAR_TOP,
  STICKY_TABLE_TOP,
  StatusChip,
  useScrolledPast,
  useWorkToolbar,
  WorkToolbar,
  type DataTableState,
  type ICompactChip,
  type IDataColumn,
  type IDataGrouping
} from '@/design';
import { useCan } from '@/domains/auth';
import type {
  IListCriteriaGroup,
  IListField,
  IListFieldCatalogue,
  IListOrder,
  IListQuery,
  IListRow,
  IReinstatementResult,
  ReinstatementDecision
} from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { LoadError, useAdminData } from '../../components/admin-page';
import { ParticipantLink } from '../../components/participant-link';
import { StateCards, useCardVariant, type IStateSignal } from '../../components/state-cards';
import { CYCLE_OPEN, LIST_MAX_PAGE_SIZE, LIST_SURVEYS, countOf, listSurveyLabel } from '../../lists';
import { NAME_FIELD } from '../../list-criteria';
import { TEAM_PATHS } from '../../paths';
import { refusalMessage } from '../../refusal';
import { entriesSince, noteEntries } from '../../notes-log';
import { NotesLog } from '../../components/notes-log';
import { REASON_CLASSES, WORKLIST_CLASSES, handlingHint, handlingSays } from '../../reinstatement';
import { SourceMark, cellText } from '../lists/cells';
import { AdvancedLink } from '../lists/filter-chips';
import { SurveyMenu, SurveyTabs } from '../lists/area';
import { nameOf } from '../lists/editing';
import { rowKeyOf } from '../lists/use-list-rows';
import {
  WorkAdvanced,
  WorkHeader,
  WorkSearch,
  quickChip,
  workFilterChips,
  workHeads,
  type IWorkScope
} from '../lists/work-page/controls';
import { useWorkColumns } from '../lists/work-page/columns';
import { WorkPageHeaderActions, useReadAt, type IHeaderRefresh } from '../lists/work-page/header-actions';
import { PAGING, WorkPager, dropPaging, readPages, useWorkPaging, type IWorkPaging } from '../lists/work-page/pager';
import { SelectionBar } from '../lists/work-page/selection-bar';
import { signalsOn, toggledSignals, useCountedFilter } from '../lists/work-page/signals';
import { useWorkPage, type IWorkPage } from '../lists/work-page/use-work-page';
import { reasonSignals, signalLines, useReasonSignals } from './card-signals';

/**
 * Reinstatement (SR-313; survey-api's `docs/superpowers/specs/2026-10-13-reinstatement-design.md`
 * §6) — PRS v1.6 WF-08's sweep: the advisors out for a reason the platform reconsiders each cycle,
 * one section per reason, each drawn the way its reason's *handling* says, so nothing opens a
 * record. Who is on the worklist is the criteria survey-api's seeded *Reinstatement* view holds —
 * Travel NO under a reason whose lifecycle class comes back, not kept out since the cycle opened —
 * and a section's rows, its class and its handling are the list's own cells.
 *
 * **One card per reason**, and one for legacy Travel 2 and 3, each with its count, its handling in
 * a few words and its class; then one toolbar — the search, the quick filters, *Advanced…*, and the
 * open section's *Reinstate all*, "?" and *Columns* — and a bar of decisions while rows are
 * selected. The open card, the filters and the section's page live in the URL, and a section's
 * holds and ticks in the session, so a row's name — a link to the advisor's record — and the browser's Back return to
 * the worklist as it was. A section reads a page at a time, the latest advisor edit first, under
 * the work pages' footer (`WorkPager`); *Reinstate all* reads the open tab's whole group, under the
 * filters, before it asks.
 *
 * **A decision is survey-api's batch** (`POST /admin/reinstatements`): reinstate (Travel 1, the
 * newest score restored or the reason cleared) or keep out, every row at the Quality cell's version
 * the section read; a row a colleague changed since comes back as a conflict, and is said.
 *
 * **"Sit out the next ranking too"** (RNK-05 AC2) is read from the notes' entries of this
 * exclusion (`notes-log.ts`): a row whose entries say so is an exception, pinned in a group at
 * the table's top and left out of *Reinstate all*; the analyst moves a selected row either way, and that choice stays
 * across a re-read.
 *
 * **The page scrolls, the section's table header sticking** under the app bar: once the cards and
 * the toolbar have scrolled away, the compact bar (`condense.tsx`) draws them in the app bar's row —
 * the survey as a menu, the open reason as one chip holding the others, the search, the quick
 * filters behind *Filters*, *Advanced…*, the open section's own controls and the actions — and the
 * filters applied on a second row, as under the toolbar.
 */

/** The tab of legacy Travel 2 and 3, beside the reasons' own. */
export const LEGACY_TAB = 'legacy';

/** The URL's filters — `f` is Lists' filter, written as survey-api reads it; a filter, the sort or a
 *  tab changed drops the section's page. */
const FILTERS = ['q', 'edited', 'outreach', 'f'] as const;
/** Every filter cleared at once — *Clear all*. */
const CLEARED = Object.fromEntries(FILTERS.map((key) => [key, null]));

/** The quick filters' positions past *Any*. */
const EDITED: readonly { value: string; label: string }[] = [
  { value: 'cycle', label: 'Since the cycle opened' },
  { value: 'never', label: 'Never' }
];
const OUTREACH: readonly { value: string; label: string }[] = [
  { value: 'any', label: 'Contacted' },
  { value: 'none', label: 'None' }
];

/** A quick filter's value in its words, a value no position knows as spelled. */
function positionWords(options: readonly { value: string; label: string }[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? asSpelled(value);
}

/** The most rows survey-api's `POST /admin/reinstatements` takes at once. */
const REINSTATE_BATCH = 2000;

const SURVEYS = LIST_SURVEYS.filter((survey) => !survey.code.startsWith('CANADA'));

/** The groups of a section's table: the exceptions, pinned on top, and the rest. */
const EXCEPTION = 'exception';
const TO_DECIDE = 'decide';

/** The wording a note uses to keep an advisor out of the next ranking too. */
const SIT_OUT = /sit out|next ranking|following ranking|two years/i;

const CLASS = 'rein.class';
const HANDLING = 'rein.handling';
const REASON_SET = 'rein.reason_set_at';
const KEPT_AT = 'rein.kept_at';
const LAST_EDIT = 'sys.advisor_last_edit_at';

/** The latest advisor edit first, never-edited last: the rows a decision turns on lead the page. */
const BY_NAME: IListOrder = { field: NAME_FIELD, dir: 'asc' };
const SECTION_SORTS: IListOrder[] = [{ field: LAST_EDIT, dir: 'desc' }, BY_NAME];

/** SR-314's newest outreach of any purpose and cycle, a withdrawn attempt none. */
const LAST_OUTREACH = 'outreach.last_at';
const FIRM = 'sys.firm_name';
const IDENTIFIER = 'sys.identifier';

/** The staff fields a survey's worklist reads, found in its catalogue by their codes. */
interface IWorklistFields {
  quality: IListField;
  travel: IListField;
  notes?: IListField;
  brokerCheckFirms?: IListField;
  outreach?: IListField;
  travelNo: string;
  legacyTravel: string[];
}

function worklistFields(byCode: ReadonlyMap<string, IListField>): Nullable<IWorklistFields> {
  const fields = [...byCode.values()];
  const quality = fields.find((field) => field.code.endsWith('.quality_rating'));
  if (!quality || !byCode.has(CLASS)) return null;
  const prefix = quality.code.slice(0, quality.code.indexOf('.'));
  const travel = byCode.get(`${prefix}.travel`);
  if (!travel) return null;
  return {
    quality,
    travel,
    notes: byCode.get(`${prefix}.notes`),
    brokerCheckFirms:
      byCode.get(`${prefix}.firm_names`) ??
      fields.find((field) => field.code.startsWith(`${prefix}.lead`) && field.code.endsWith('_firm_names')),
    outreach: byCode.get(LAST_OUTREACH),
    travelNo: optionCode(travel, 'NO'),
    legacyTravel: ['2', '3'].map((code) => optionCode(travel, code))
  };
}

/** Travel's option whose code or label is `wanted`, ignoring case — as survey-api finds its NO. */
function optionCode(field: IListField, wanted: string): string {
  const lower = wanted.toLowerCase();
  return (
    field.options?.find((option) => option.code.toLowerCase() === lower || option.label.toLowerCase() === lower)
      ?.code ?? wanted
  );
}

/** The ineligible under a worklist class, not kept out since the cycle opened, and `also`. */
function ineligible(fields: IWorklistFields, ...also: IListCriteriaGroup['children']): IListCriteriaGroup {
  return {
    version: 1,
    op: 'and',
    children: [
      { field: fields.travel.code, op: 'eq', value: fields.travelNo },
      { field: CLASS, op: 'in', value: [...WORKLIST_CLASSES] },
      {
        op: 'or',
        children: [
          { field: KEPT_AT, op: 'empty' },
          { field: KEPT_AT, op: 'before', value: CYCLE_OPEN }
        ]
      },
      ...also
    ]
  };
}

function legacyTravel(fields: IWorklistFields, ...also: IListCriteriaGroup['children']): IListCriteriaGroup {
  return {
    version: 1,
    op: 'and',
    children: [{ field: fields.travel.code, op: 'in', value: fields.legacyTravel }, ...also]
  };
}

/** The filters the URL holds, as criteria: a name, firm or CRD; the advisor's edits; outreach. */
function filterCriteria(params: URLSearchParams, fields: IWorklistFields): IListCriteriaGroup['children'] {
  const leaves: IListCriteriaGroup['children'] = [];
  const text = params.get('q')?.trim();
  if (text) {
    leaves.push({
      op: 'or',
      children: [NAME_FIELD, FIRM, IDENTIFIER].map((field) => ({ field, op: 'contains', value: text }))
    });
  }
  const edited = params.get('edited');
  if (edited === 'cycle') leaves.push({ field: LAST_EDIT, op: 'since', value: CYCLE_OPEN });
  if (edited === 'never') leaves.push({ field: LAST_EDIT, op: 'empty' });
  const outreach = params.get('outreach');
  if (fields.outreach && outreach === 'none') leaves.push({ field: fields.outreach.code, op: 'empty' });
  if (fields.outreach && outreach === 'any') leaves.push({ field: fields.outreach.code, op: 'not_empty' });
  return leaves;
}

/** `params` with `changes` applied, and the page dropped: a new tab or filter starts at the top. */
function withParams(params: URLSearchParams, changes: Record<string, Nullable<string>>): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const [key, value] of Object.entries(changes)) {
    if (value) next.set(key, value);
    else next.delete(key);
  }
  return dropPaging(next);
}

/** A section's rows as `paging` shows them — its page, and the pages *Load more* added. */
async function readSection(
  query: IListQuery,
  paging: IWorkPaging,
  signal: AbortSignal
): Promise<{ rows: IListRow[]; total: number }> {
  const parts = await readPages(
    (page, pageSize) => listsApi.rows({ query, page, pageSize }, signal),
    paging.page,
    paging.perPage,
    paging.pages,
    LIST_MAX_PAGE_SIZE
  );
  return { rows: parts.flatMap((part) => part.rows), total: parts[0].total };
}

/**
 * A section's holds or ticks, kept in the session under `key` for the history entry they were
 * made on: Back from a record finds them again, and a fresh visit — the menu, a link — starts
 * clean. The entry's own key moves when the URL is replaced, so it is written again then.
 */
function useSessionState<T extends Iterable<unknown>>(key: string, fromJson: (json: unknown) => T) {
  const entry = useLocation().key;
  const [value, setValue] = React.useState<T>(() => {
    try {
      const kept = JSON.parse(sessionStorage.getItem(key) ?? 'null') as Nullable<{ at: string; value: unknown }>;
      return fromJson(kept?.at === entry ? kept.value : []);
    } catch {
      return fromJson([]);
    }
  });
  React.useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify({ at: entry, value: [...value] }));
    } catch {
      // A full or refused store keeps the state for this visit only.
    }
  }, [key, entry, value]);
  return [value, setValue] as const;
}

const asKeys = (json: unknown): ReadonlySet<string> =>
  new Set(Array.isArray(json) ? json.filter((key): key is string => typeof key === 'string') : []);
const asHolds = (json: unknown): ReadonlyMap<string, boolean> =>
  new Map(
    Array.isArray(json)
      ? json.filter(
          (pair): pair is [string, boolean] =>
            Array.isArray(pair) && typeof pair[0] === 'string' && typeof pair[1] === 'boolean'
        )
      : []
  );

/** What a table with no row to draw says: a failed read, a read under way, or `empty`. */
function tableState(
  read: { error: Nullable<string>; data: unknown },
  empty: string,
  shown: number
): DataTableState | undefined {
  if (read.error) return { kind: 'error', content: <LoadError message={read.error} /> };
  if (!read.data) return { kind: 'empty', content: 'Reading the rows…' };
  return shown === 0 ? { kind: 'empty', content: empty } : undefined;
}

export function AdminReinstatementPage() {
  const [params] = useSearchParams();
  const survey = params.get('survey') || 'FA';
  const { data, error, reload, reading } = useAdminData(
    async (signal) => ({ survey, catalogue: await listsApi.fields(survey, signal) }),
    [survey]
  );
  const readAt = useReadAt(data);
  const answer = data?.survey === survey ? data : null;
  // The worklist's one line and its seeded view, said in the header once read.
  const [about, setAbout] = React.useState<Nullable<IWorklistAbout>>(null);
  const lists = about?.view ? { to: `${TEAM_PATHS.lists}?${new URLSearchParams({ survey, view: about.view })}` } : null;
  // Until the worklist has said itself, Refresh reads the catalogue it starts from again.
  const refresh = about?.refresh ?? { reading, readAt, onClick: () => void reload() };
  const help = (
    <HelpPopover label="About Reinstatement" align="end">
      <p>
        The advisors out for a reason that comes back each cycle, by reason — with the notes, dates and firms
        each decision needs.
      </p>
      <p>
        Each card is a reason with its count under the filters; its badges count the advisors in it who edited
        the survey this cycle, have had no outreach, or whose notes hold a sit-out phrase — press one to show
        only those, and another to show those too; press it again, or its ✕, to drop it — the counts stay as
        they are. Hover a card for what to do and its class.
      </p>
      <p>
        Scrolled down, the header, the cards and the filters ride slimmed in the bar at the top of the window,
        with the table’s column names under them.
      </p>
    </HelpPopover>
  );

  return (
    <>
      <PageHeader
        title="Reinstatement"
        row
        tabs={<SurveyTabs current={survey} surveys={SURVEYS} />}
        actions={
          <WorkPageHeaderActions
            summary={about?.summary ?? null}
            summaryData={{ 'data-worklist-summary': '' }}
            lists={lists}
            refresh={refresh}
            help={help}
          />
        }
      />
      <div className="grid min-w-0 gap-3 px-6 py-4">
        {error ? (
          <LoadError message={error} />
        ) : answer === null ? (
          <p className="text-sm text-muted-foreground">Reading the worklist…</p>
        ) : answer.catalogue === null ? (
          <p className="text-sm text-muted-foreground">This deployment holds no {listSurveyLabel(survey)} survey.</p>
        ) : (
          <Worklist
            key={survey}
            survey={survey}
            catalogue={answer.catalogue}
            onAbout={setAbout}
            actions={<WorkPageHeaderActions summary={null} lists={lists} refresh={refresh} help={help} />}
          />
        )}
      </div>
    </>
  );
}

/** What the header says of the worklist: its count in one line, its seeded view in Lists, and its
 *  *Refresh*. */
interface IWorklistAbout {
  summary: string;
  view: Nullable<string>;
  refresh: IHeaderRefresh;
}

/** A reason's handling and lifecycle class, as its rows hold them — what its card says. */
interface IReasonKind {
  handling: string;
  lifecycle: string;
}

function Worklist({
  survey,
  catalogue,
  onAbout,
  actions
}: {
  survey: string;
  catalogue: IListFieldCatalogue;
  onAbout: (about: Nullable<IWorklistAbout>) => void;
  /** The header's actions, as the compact bar draws them. */
  actions: React.ReactNode;
}) {
  const [params, setParams] = useSearchParams();
  const page = useWorkPage(survey, catalogue, { drop: PAGING });
  const byCode = page.fields;
  const fields = React.useMemo(() => worklistFields(byCode), [byCode]);
  const [text, edited, contacted] = FILTERS.map((key) => params.get(key));
  // The worklist's own filters — the funnels' base — and with them Lists' filter, the rows'.
  const own = React.useMemo(
    () => (fields === null ? [] : filterCriteria(params, fields)),
    // The filters' own values, not the page's or the tab's.
    [fields, text, edited, contacted]
  );
  const filters = React.useMemo(() => (page.filter ? [...own, page.filter] : own), [own, page.filter]);
  const outreachCode = fields?.outreach?.code ?? null;
  const notesCode = fields?.notes?.code ?? null;
  // Each signal's condition, which its press adds to the signals' group in Lists' filter.
  const workSignals = Object.values(reasonSignals(outreachCode, notesCode)).flatMap((spec) => spec ?? []);
  // The cards count under the filters but the signals pressed, which narrow the rows alone.
  const countedFilter = useCountedFilter(page, workSignals);
  const counting = React.useMemo(() => (countedFilter ? [...own, countedFilter] : own), [own, countedFilter]);
  const [reads, setReads] = React.useState(0);
  // Moved by the header's Refresh: every read the worklist draws is read again.
  const [reread, setReread] = React.useState(0);
  const [said, setSaid] = React.useState('');
  const onDecided = React.useCallback((message: string) => {
    setSaid(message);
    setReads((count) => count + 1);
  }, []);
  // The counts follow the filters but the signals; only the reasons' move with a decision, and the
  // seeded view with neither.
  const reasons = useAdminData(
    async (signal) =>
      fields === null
        ? null
        : (
            await listsApi.facets(
              { query: { survey, criteria: ineligible(fields, ...counting) }, field: fields.quality.code },
              signal
            )
          ).values.filter((value) => typeof value.value === 'string' && value.count > 0),
    [survey, fields, counting, reads, reread]
  );
  const legacyCount = useAdminData(
    async (signal) =>
      fields === null
        ? null
        : (
            await listsApi.aggregates(
              { query: { survey, criteria: legacyTravel(fields, ...counting) }, fields: [] },
              signal
            )
          ).count,
    [survey, fields, counting, reread]
  );
  const seeded = useAdminData(
    async (signal) => ({
      view: (await listsApi.listViews(survey, 'list', signal))?.find((view) => view.seed === 'reinstatement')?.id ?? null
    }),
    [survey, reread]
  );
  // Each reason's handling and class, as its rows hold them — read once; a failed read leaves the
  // cards without their few words, and the page works the same.
  const kinds = useAdminData(
    async (signal) => {
      if (fields === null) return null;
      const answer = await listsApi.aggregates(
        {
          query: {
            survey,
            criteria: ineligible(fields),
            groupBy: [{ field: fields.quality.code }, { field: HANDLING }, { field: CLASS }]
          },
          fields: []
        },
        signal
      );
      const by = new Map<string, IReasonKind>();
      for (const group of answer.groups) {
        const [reason, handling, lifecycle] = group.keys;
        if (group.keys.length === 3 && typeof reason === 'string') {
          by.set(reason, { handling: String(handling ?? 'per_record'), lifecycle: String(lifecycle ?? '') });
        }
      }
      return by;
    },
    [survey, fields]
  );
  const counted = reasons.data ?? [];
  const legacy = legacyCount.data ?? 0;
  const view = seeded.data?.view ?? null;
  const total = counted.reduce((sum, reason) => sum + reason.count, 0);
  const summary =
    reasons.data == null || legacyCount.data == null
      ? null
      : `${total.toLocaleString('en-US')} to reconsider, in ${countOf(counted.length, 'reason')}${
          legacy > 0 ? `; ${legacy.toLocaleString('en-US')} on legacy Travel 2 or 3` : ''
        }.`;
  const readAt = useReadAt(reasons.data);
  const reading = reasons.reading || legacyCount.reading;
  const readAgain = React.useCallback(() => setReread((count) => count + 1), []);
  React.useEffect(() => {
    onAbout(summary === null ? null : { summary, view, refresh: { reading, readAt, onClick: readAgain } });
    return () => onAbout(null);
  }, [summary, view, reading, readAt, readAgain, onAbout]);
  const [variant] = useCardVariant();
  const handlings = React.useMemo(
    () => (kinds.data ? new Map([...kinds.data].map(([reason, kind]) => [reason, kind.handling])) : null),
    [kinds.data]
  );
  const cardKeys = [...counted.map((reason) => String(reason.value)), ...(legacy > 0 ? [LEGACY_TAB] : [])];
  const openKey = cardKeys.find((key) => key === params.get('tab')) ?? cardKeys[0];
  const pressed = signalsOn(page, workSignals).map((one) => one.key);
  const signals = useReasonSignals({
    survey,
    cards: cardKeys,
    filters: counting,
    scope:
      fields === null
        ? null
        : {
            op: 'or',
            children: [
              { op: 'and', children: ineligible(fields).children },
              { op: 'and', children: legacyTravel(fields).children }
            ]
          },
    handlings,
    criteriaFor: (card, extra) =>
      fields === null
        ? { version: 1, op: 'and', children: [] }
        : card === LEGACY_TAB
          ? legacyTravel(fields, ...extra)
          : ineligible(fields, { field: fields.quality.code, op: 'eq', value: card }, ...extra),
    outreach: outreachCode,
    notes: notesCode,
    variant,
    counted: JSON.stringify([counting, reads, reread]),
    on: (card, key) => card === openKey && pressed.includes(key)
  });
  // The section's own controls — *Reinstate all*, its "?", *Columns* — drawn at the toolbar's end,
  // and in the compact bar while it shows.
  const [slot, setSlot] = React.useState<Nullable<HTMLElement>>(null);
  const [barSlot, setBarSlot] = React.useState<Nullable<HTMLElement>>(null);
  const asked = params.get('q') ?? '';
  const [draft, setDraft] = React.useState(asked);
  // Back and Forward move the search the URL holds; the box follows it.
  React.useEffect(() => setDraft(asked), [asked]);
  // Where the page's own controls end: scrolled under the app bar, the compact bar stands for them.
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);
  const toolbar = useWorkToolbar();
  const [filtersOpen, setFiltersOpen] = React.useState(false);

  if (fields === null) {
    return (
      <p className="text-sm text-muted-foreground">
        {listSurveyLabel(survey)}’s list has no Quality Rating and Travel with their reason classes, so there is
        nothing to reconsider.
      </p>
    );
  }
  // A filter survey-api refuses is said under the filters, which stay to be changed.
  const error = reasons.error ?? legacyCount.error ?? seeded.error;
  if (error && filters.length === 0) return <LoadError message={error} />;
  if (!error && (reasons.data == null || legacyCount.data == null || seeded.data == null)) {
    return <p className="text-sm text-muted-foreground">Reading the worklist…</p>;
  }
  const tabs = [
    ...counted.map((reason) => ({
      key: String(reason.value),
      label: reason.label ?? String(reason.value),
      count: reason.count
    })),
    ...(legacy > 0 ? [{ key: LEGACY_TAB, label: 'Legacy Travel 2 or 3', count: legacy }] : [])
  ];
  const open = tabs.find((tab) => tab.key === params.get('tab')) ?? tabs[0];
  const search = {
    label: 'Filter the worklist',
    placeholder: 'Filter the worklist…',
    filters: true,
    draft,
    onDraft: setDraft,
    onSubmit: () => setParams(withParams(params, { q: draft.trim() || null }))
  };
  // Each card's signals as toggles: a press adds its condition to the signals' group, opening its reason.
  const toggles =
    signals &&
    Object.fromEntries(
      tabs.map((tab) => [
        tab.key,
        toggledSignals({
          page,
          params,
          setParams: (next) => setParams(dropPaging(next)),
          current: tab.key === open?.key,
          signals: signals[tab.key],
          specs: workSignals,
          pressed,
          openCard: (next) => next.set('tab', tab.key),
          nameOf: (signal) => `Show ${signal.count.toLocaleString('en-US')} ${signal.label} in ${tab.label}`
        }) ?? []
      ])
    );
  const cards = open && (
    <ReasonCards
      params={params}
      tabs={tabs}
      open={open.key}
      kinds={kinds.data ?? null}
      classField={byCode.get(CLASS)}
      signals={toggles}
      pending={(key) => signalLines(handlings?.get(key) ?? null, variant, outreachCode, notesCode)}
      total={total + legacy}
    />
  );
  const clearAll = () => setParams(withParams(params, CLEARED));
  // Every filter applied, as a chip with its value — the page's row under the toolbar and the
  // compact bar's second row alike: a quick filter's press opens its control, a Lists chip its
  // column's funnel.
  const openFilters = () => {
    setFiltersOpen(true);
    return true as const;
  };
  const clear = (key: string) => () => setParams(withParams(params, { [key]: null }));
  const quickChipsWith = (press: (label: string) => true): ICompactChip[] => [
    ...(edited ? [quickChip('Advisor edits', positionWords(EDITED, edited), press, clear('edited'))] : []),
    ...(fields.outreach && contacted
      ? [quickChip('Outreach', positionWords(OUTREACH, contacted), press, clear('outreach'))]
      : [])
  ];
  const quickCount = quickChipsWith(openFilters).length;
  const appliedChips = workFilterChips(page, workSignals, quickChipsWith(openFilters));
  const pageChips = workFilterChips(page, workSignals, quickChipsWith(toolbar.openQuick));
  const quickFilters = <QuickFilters params={params} outreach={fields.outreach !== undefined} />;

  return (
    <div className="grid gap-3">
      <CompactBar
        shown={scrolled && open !== undefined}
        label="Reinstatement, compact"
        actions={actions}
        below={appliedChips.length > 0 && <CompactChipLine chips={appliedChips} onClearAll={clearAll} />}
      >
        <CompactTitle>Reinstatement</CompactTitle>
        <SurveyMenu current={survey} surveys={SURVEYS} />
        {open && (
          <CompactMenu
            label="Reasons"
            chip
            trigger={
              <>
                <span className="max-w-48 truncate">{open.label}</span>
                <span className="font-semibold tabular-nums">{open.count.toLocaleString('en-US')}</span>
              </>
            }
            items={tabs.map((tab) => ({
              key: tab.key,
              label: tab.label,
              to: { search: withParams(params, { tab: tab.key }).toString() },
              current: tab.key === open.key,
              count: tab.count.toLocaleString('en-US')
            }))}
          />
        )}
        <CompactDivider />
        <CompactSearch {...search} />
        <CompactFilters count={quickCount} open={filtersOpen} onOpenChange={setFiltersOpen}>
          {quickFilters}
        </CompactFilters>
        <AdvancedLink onPress={() => page.setAdvanced({ open: true })} />
        <span className="flex-1" />
        <CompactDivider />
        <span ref={setBarSlot} className="flex items-center gap-2" />
        <CompactDivider />
      </CompactBar>
      <p role="status" className="text-[0.8125rem] text-[var(--color-teal)] empty:hidden">
        {said}
      </p>
      {open === undefined && filters.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nobody is waiting to be reconsidered.</p>
      ) : (
        <>
          {cards}
          <WorkToolbar
            toolbar={toolbar}
            search={<WorkSearch search={search} />}
            quick={quickFilters}
            quickCount={quickCount}
            after={<WorkAdvanced page={page} />}
            end={<span ref={setSlot} className="flex items-center gap-2" />}
          />
          {pageChips.length > 0 && <CompactChipLine label="Filters" chips={pageChips} onClearAll={clearAll} />}
          <div ref={setControlsEnd} aria-hidden data-controls-end="" className="-mt-3 h-0" />
          {error ? (
            <LoadError message={error} />
          ) : open === undefined ? (
            <p className="text-sm text-muted-foreground">Nobody on the worklist matches the filters.</p>
          ) : open.key === LEGACY_TAB ? (
            <LegacyTravelSection
              survey={survey}
              fields={fields}
              byCode={byCode}
              count={legacy}
              own={own}
              filters={filters}
              page={page}
              slot={slot}
              barSlot={barSlot}
              reread={reread}
              signalled={pressed.length > 0}
            />
          ) : (
            <ReasonSection
              key={open.key}
              survey={survey}
              fields={fields}
              byCode={byCode}
              reason={open.key}
              label={open.label}
              count={open.count}
              own={own}
              filters={filters}
              page={page}
              slot={slot}
              barSlot={barSlot}
              reread={reread}
              onDecided={onDecided}
              signalled={pressed.length > 0}
            />
          )}
        </>
      )}
    </div>
  );
}

/** The worklist's lists as cards, a reason's or legacy Travel's each — its count, what to do in a
 *  few words, its class — the open one by the URL's `tab`. */
function ReasonCards({
  params,
  tabs,
  open,
  kinds,
  classField,
  signals,
  pending,
  total
}: {
  params: URLSearchParams;
  tabs: readonly { key: string; label: string; count: number }[];
  open: string;
  kinds: Nullable<ReadonlyMap<string, IReasonKind>>;
  classField: IListField | undefined;
  signals: Nullable<Record<string, IStateSignal[]>>;
  /** The signal lines a card holds while its signals are read. */
  pending: (key: string) => number;
  total: number;
}) {
  return (
    <StateCards
      label="Reasons"
      compact
      cards={tabs.map((tab) => {
        const kind = kinds?.get(tab.key);
        const lifecycle = kind && classField ? cellText(classField, { v: kind.lifecycle }) : '';
        const short = REASON_CLASSES.find((one) => one.value === kind?.lifecycle)?.short ?? lifecycle;
        const hint = tab.key === LEGACY_TAB ? 'check thresholds, set Travel' : kind && handlingHint(kind.handling);
        return {
          key: tab.key,
          to: { search: withParams(params, { tab: tab.key }).toString() },
          current: tab.key === open,
          label: tab.label,
          count: tab.count.toLocaleString('en-US'),
          hint,
          chip: short ? <StatusChip>{short}</StatusChip> : undefined,
          tooltip: [tab.label, lifecycle, hint].filter(Boolean).join(' · '),
          signals: signals?.[tab.key],
          signalsPending: signals ? undefined : pending(tab.key),
          share: total > 0 ? tab.count / total : undefined,
          data: { 'data-tab': tab.key }
        };
      })}
    />
  );
}

/** The worklist's quick filters, drawn in the toolbar and again in the compact bar. */
function QuickFilters({ params, outreach }: { params: URLSearchParams; outreach: boolean }) {
  const [, setParams] = useSearchParams();
  const set = (changes: Record<string, Nullable<string>>) => setParams(withParams(params, changes));
  return (
    <>
      <SelectChip
        label="Advisor edits"
        any="Any"
        value={params.get('edited')}
        options={EDITED}
        onChoose={(value) => set({ edited: value })}
      />
      {outreach && (
        <SelectChip
          label="Outreach"
          any="Any"
          value={params.get('outreach')}
          options={OUTREACH}
          onChoose={(value) => set({ outreach: value })}
        />
      )}
    </>
  );
}

/** One worklist section: its rows and what it offers, under the card that names it. */
function WorklistSection({ reason, title, children }: { reason: string; title: string; children: React.ReactNode }) {
  return (
    <section aria-label={title} data-reason={reason} className="grid gap-2">
      {children}
    </section>
  );
}

/** The cell's value was migrated from Salesforce (SR-457). */
function fromSalesforce(row: IListRow, code: string): boolean {
  return row.cells[code]?.src === 'salesforce';
}

function instant(row: IListRow, code: string): Nullable<number> {
  const value = row.cells[code]?.v;
  return typeof value === 'string' ? Date.parse(value) : null;
}

function firmKey(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/** What a row says for the decision, worked out once per read. */
interface IRowFacts {
  /** The advisor has edited the survey since the later of the reason and the last outreach. */
  editedSince: boolean;
  /** Its notes say to sit out the next ranking too — in an entry of this exclusion's, not an
   *  earlier one's (`entriesSince`). */
  sitsOut: boolean;
  /** The survey's firm is among BrokerCheck's — survey-api joins them with ", ", and a firm's
   *  own name may hold a comma, so the match is a whole run of words. Null where either is
   *  unknown. */
  firmsMatch: Nullable<boolean>;
}

function factsOf(row: IListRow, fields: IWorklistFields): IRowFacts {
  const edited = instant(row, LAST_EDIT);
  const set = instant(row, REASON_SET);
  const contacted = instant(row, LAST_OUTREACH);
  const since = set === null ? null : Math.max(set, contacted ?? set);
  const notes = fields.notes ? row.cells[fields.notes.code]?.v : undefined;
  const survey = row.cells[FIRM]?.v;
  const brokerCheck = fields.brokerCheckFirms ? row.cells[fields.brokerCheckFirms.code]?.v : undefined;
  const surveyKey = typeof survey === 'string' ? firmKey(survey) : '';
  return {
    editedSince: edited !== null && since !== null && edited > since,
    sitsOut:
      typeof notes === 'string' &&
      entriesSince(noteEntries(notes), set ?? Date.now()).some((entry) => SIT_OUT.test(entry.text)),
    firmsMatch:
      surveyKey && typeof brokerCheck === 'string' && brokerCheck
        ? ` ${firmKey(brokerCheck)} `.includes(` ${surveyKey} `)
        : null
  };
}

/** The rows a reinstatement suggests at once: an edit since the reason, and for Left Firm firms
 *  that match too (WF-08 5 and 8). */
function suggested(facts: IRowFacts, handling: string): boolean {
  if (handling === 'firm_compare') return facts.editedSince && facts.firmsMatch === true;
  return handling === 'edit_vs_outreach' && facts.editedSince;
}

const ReasonSection = React.memo(function ReasonSection({
  survey,
  fields,
  byCode,
  reason,
  label,
  count,
  own,
  filters,
  page,
  slot,
  barSlot,
  reread,
  onDecided,
  signalled
}: {
  survey: string;
  fields: IWorklistFields;
  byCode: ReadonlyMap<string, IListField>;
  reason: string;
  label: string;
  count: number;
  /** The worklist's own filters, without Lists' — what the funnels count under, with the reason. */
  own: IListCriteriaGroup['children'];
  /** The worklist's filters, as criteria — the section's rows and *Reinstate all* both obey them. */
  filters: IListCriteriaGroup['children'];
  /** Lists' controls: the filter, the column sort — none keeps the latest advisor edit first — and
   *  the extra columns. */
  page: IWorkPage;
  /** The toolbar's end, where the section draws its own controls. */
  slot: Nullable<HTMLElement>;
  /** The compact bar's place for them, while it shows. */
  barSlot: Nullable<HTMLElement>;
  /** Moved by the header's Refresh: the rows are read again. */
  reread: number;
  onDecided: (message: string) => void;
  /** Whether a card's signal is pressed, narrowing the rows. */
  signalled: boolean;
}) {
  const can = useCan();
  const canWrite = can('internal_fields.write');
  const notPermitted = React.useId();
  const notes = fields.notes?.code;
  const firms = fields.brokerCheckFirms?.code;
  const outreach = fields.outreach?.code;
  const paging = useWorkPaging({ tab: reason, replace: true });
  const { sorts } = page;
  const criteria = ineligible(fields, { field: fields.quality.code, op: 'eq', value: reason }, ...filters);
  const scope = React.useMemo<IWorkScope>(
    () => ({
      query: { survey },
      base: ineligible(fields, { field: fields.quality.code, op: 'eq', value: reason }, ...own)
    }),
    [survey, fields, reason, own]
  );
  const ownColumns = [
    NAME_FIELD,
    FIRM,
    IDENTIFIER,
    fields.quality.code,
    CLASS,
    HANDLING,
    REASON_SET,
    LAST_EDIT,
    ...(notes ? [notes] : []),
    ...(firms ? [firms] : []),
    ...(outreach ? [outreach] : [])
  ];
  // The viewer's columns for this reason, kept on this browser.
  const chosen = useWorkColumns(page, `reinstatement.${survey}.${reason}`, ownColumns);
  const readColumns = [...ownColumns, ...chosen.extras];
  const read = useAdminData(
    (signal) =>
      readSection(
        { survey, criteria, columns: readColumns, sorts: sorts.length > 0 ? [...sorts, BY_NAME] : SECTION_SORTS },
        paging,
        signal
      ),
    [survey, reason, filters, sorts, chosen.extras.join(','), paging.page, paging.perPage, paging.pages, reread]
  );
  const loaded = read.data?.rows ?? null;
  const first = loaded?.[0]?.cells;
  const handlingCell = first?.[HANDLING]?.v;
  const handling = typeof handlingCell === 'string' ? handlingCell : 'per_record';
  const classField = byCode.get(CLASS);
  const lifecycle = classField ? cellText(classField, first?.[CLASS]) : '';
  // A class that waits on the advisor reads the last edit, and puts those who edited since first —
  // unless the analyst sorts by a column, when the order is survey-api's across the pages.
  const readsEdits =
    first?.[CLASS]?.v === 'on_resubmission' || ['bulk', 'firm_compare', 'edit_vs_outreach'].includes(handling);
  const facts = React.useMemo(
    () => new Map((loaded ?? []).map((row) => [rowKeyOf(row), factsOf(row, fields)])),
    [loaded, fields]
  );
  const rows = React.useMemo(
    () =>
      loaded && readsEdits && sorts.length === 0
        ? [...loaded].sort(
            (left, right) =>
              Number(facts.get(rowKeyOf(right))!.editedSince) - Number(facts.get(rowKeyOf(left))!.editedSince)
          )
        : loaded,
    [loaded, readsEdits, sorts, facts]
  );

  // The analyst's own holds and releases, by row; a row with none is held where its notes say so.
  const kept = `veritant.reinstatement.${survey}.${reason}`;
  const [moved, setMoved] = useSessionState(`${kept}.moved`, asHolds);
  const [ticked, setTicked] = useSessionState(`${kept}.ticked`, asKeys);
  const [asking, setAsking] = React.useState<Nullable<{ decision: ReinstatementDecision; rows: IListRow[] }>>(null);
  // Suggested rows are ticked the first time they are read, so paging — and a record's visit —
  // keeps the analyst's own ticks.
  const [seen, setSeen] = useSessionState(`${kept}.seen`, asKeys);
  React.useEffect(() => {
    if (handling !== 'firm_compare' && handling !== 'edit_vs_outreach') return;
    const fresh = (rows ?? []).filter((row) => !seen.has(rowKeyOf(row)));
    if (fresh.length === 0) return;
    setSeen((current) => new Set([...current, ...fresh.map(rowKeyOf)]));
    const suggest = fresh.filter((row) => suggested(facts.get(rowKeyOf(row))!, handling)).map(rowKeyOf);
    if (suggest.length > 0) setTicked((current) => new Set([...current, ...suggest]));
    // `seen` is read, not followed: it grows from this effect alone.
  }, [rows, facts, handling]);
  // A row off the page — *Reinstate all*'s — has its facts worked out where it is asked about.
  const isHeld = (row: IListRow) =>
    moved.get(rowKeyOf(row)) ??
    (handling === 'bulk_except_noted' && (facts.get(rowKeyOf(row)) ?? factsOf(row, fields)).sitsOut);

  const pageColumns = React.useMemo(() => {
    const text = (row: IListRow, code: string) => {
      const field = byCode.get(code);
      return field ? cellText(field, row.cells[code]) : '';
    };
    const head = workHeads(page, scope);
    const shown: IDataColumn<IListRow>[] = [
      { id: 'name', ...head(NAME_FIELD, 'Name'), cell: (row) => <RecordLink row={row} name={text(row, NAME_FIELD)} /> },
      { id: 'firm', ...head(FIRM, 'Firm'), optional: true, cell: (row) => text(row, FIRM) },
      {
        id: 'identifier',
        ...head(IDENTIFIER, byCode.get(IDENTIFIER)?.label ?? 'Identifier'),
        optional: true,
        cell: (row) => text(row, IDENTIFIER)
      },
      {
        id: 'set',
        ...head(REASON_SET, 'Reason set'),
        optional: true,
        cell: (row) => (
          <>
            {text(row, REASON_SET)}
            {fromSalesforce(row, REASON_SET) && <SourceMark says="From Salesforce field history" />}
          </>
        )
      }
    ];
    shown.push({
      id: 'edited',
      ...head(LAST_EDIT, 'Advisor last edit'),
      // Off until asked for where the class does not wait on the advisor.
      ...offUnless(readsEdits),
      cell: (row) => (
        <span className="inline-flex items-center gap-2">
          <span>
            {text(row, LAST_EDIT) || 'Never'}
            {fromSalesforce(row, LAST_EDIT) && <SourceMark says="From Salesforce (Advisor Last Edit)" />}
          </span>
          {facts.get(rowKeyOf(row))?.editedSince && <StatusChip tone="success">Edited since</StatusChip>}
        </span>
      )
    });
    if (outreach) {
      shown.push({
        id: 'outreach',
        ...head(outreach, 'Last outreach'),
        ...offUnless(handling === 'edit_vs_outreach' || handling === 'firm_compare'),
        cell: (row) => (
          <>
            {text(row, outreach) || 'None'}
            {fromSalesforce(row, outreach) && <SourceMark />}
          </>
        )
      });
    }
    if (handling === 'firm_compare') {
      shown.push({
        id: 'brokercheck',
        ...(firms ? head(firms, 'BrokerCheck firm') : { header: 'BrokerCheck firm' }),
        cell: (row) => {
          const match = facts.get(rowKeyOf(row))?.firmsMatch ?? null;
          return (
            <span className="inline-flex items-center gap-2">
              {firms ? text(row, firms) : ''}
              {match !== null && (
                <StatusChip tone={match ? 'success' : 'attention'}>{match ? 'Firms match' : 'Firms differ'}</StatusChip>
              )}
            </span>
          );
        }
      });
    }
    if (notes) {
      shown.push({
        // The notes' log is drawn here, so its header sorts and filters nothing.
        id: 'notes',
        header: 'Notes',
        ...offUnless(handling !== 'bulk'),
        className: 'min-w-[16rem]',
        cell: (row) => {
          const value = row.cells[notes]?.v;
          return typeof value === 'string' ? (
            <NotesLog notes={value} startOpen={facts.get(rowKeyOf(row))?.sitsOut ?? false} />
          ) : '';
        }
      });
    }
    if (classField) {
      shown.push({
        id: 'class',
        ...head(CLASS, classField.label),
        ...offUnless(false),
        cell: (row) => cellText(classField, row.cells[CLASS])
      });
    }
    return shown;
  }, [byCode, classField, readsEdits, handling, facts, firms, notes, outreach, page, scope]);
  const columns = chosen.resolve(pageColumns, scope);

  // The exceptions are pinned in a group of their own at the table's top, over the rest.
  const exceptions = new Set((rows ?? []).filter(isHeld).map(rowKeyOf));
  const main = (rows ?? []).filter((row) => !exceptions.has(rowKeyOf(row)));
  const [folded, setFolded] = React.useState<ReadonlySet<string>>(new Set());
  const grouping: IDataGrouping<IListRow> = {
    levels: [
      {
        id: 'exception',
        label: 'Exceptions',
        value: (row) => (exceptions.has(rowKeyOf(row)) ? EXCEPTION : TO_DECIDE),
        compare: (left, right) => Number(left === TO_DECIDE) - Number(right === TO_DECIDE),
        name: (value) =>
          value === EXCEPTION ? 'Exceptions — read the note: left out of Reinstate all' : 'To decide'
      }
    ],
    collapsed: folded,
    onToggle: (key) =>
      setFolded((now) => {
        const next = new Set(now);
        if (!next.delete(key)) next.add(key);
        return next;
      })
  };
  const tickedRows = (rows ?? []).filter((row) => ticked.has(rowKeyOf(row)));
  const bulk = handling === 'bulk' || handling === 'bulk_except_noted';
  const decides = handling !== 'none';
  const selection = decides
    ? { selected: ticked, onSelect: (keys: string[]) => setTicked(new Set(keys)), name: nameOf }
    : undefined;
  const gated = {
    type: 'button' as const,
    size: 'sm' as const,
    'aria-describedby': canWrite ? undefined : notPermitted
  };
  // *Reinstate all* is the group, not the page: every row is read — only what the decision and the
  // holds need — and those held back, by the notes or by the analyst, are left out before it asks.
  const [gathering, setGathering] = React.useState(false);
  const [gatherError, setGatherError] = React.useState<Nullable<string>>(null);
  const reinstateAll = async () => {
    setGathering(true);
    setGatherError(null);
    try {
      const columns = [
        fields.quality.code,
        ...(handling === 'bulk_except_noted' && notes ? [notes, REASON_SET] : [])
      ];
      const pageOf = (page: number) =>
        listsApi.rows({ query: { survey, criteria, columns, sorts: SECTION_SORTS }, page, pageSize: LIST_MAX_PAGE_SIZE });
      const first = await pageOf(1);
      const rest = await Promise.all(
        Array.from({ length: Math.ceil(first.total / LIST_MAX_PAGE_SIZE) - 1 }, (_, at) => pageOf(at + 2))
      );
      const going = [first, ...rest].flatMap((page) => page.rows).filter((row) => !isHeld(row));
      if (going.length === 0) setGatherError('Every row of this group is held back.');
      else setAsking({ decision: 'reinstate', rows: going });
    } catch (error) {
      setGatherError(refusalMessage(error, 'Could not read the group'));
    } finally {
      setGathering(false);
    }
  };
  const move = (hold: boolean) => {
    setMoved((current) => new Map([...current, ...tickedRows.map((row) => [rowKeyOf(row), hold] as const)]));
    setTicked(new Set());
  };

  return (
    <WorklistSection reason={reason} title={label}>
      <SectionControls slot={slot} barSlot={barSlot}>
        <HelpPopover label={`About ${label}`} align="end">
          <p>{handlingSays(handling)}</p>
          {lifecycle && <p>{lifecycle}.</p>}
          {handling === 'bulk_except_noted' && (
            <p>
              The Exceptions group reads only the notes’ entries since the reason was set; the card’s “sit-out
              phrase in notes” counts the phrase anywhere in the notes.
            </p>
          )}
        </HelpPopover>
        {decides && bulk && (
          <Button
            {...gated}
            variant="outline"
            disabled={
              !canWrite ||
              gathering ||
              (rows !== null && rows.length >= (read.data?.total ?? count) && main.length === 0)
            }
            onClick={() => void reinstateAll()}
          >
            {gathering ? 'Reading the group…' : filters.length > 0 ? 'Reinstate all matching…' : 'Reinstate all…'}
          </Button>
        )}
        {chosen.button(pageColumns)}
      </SectionControls>
      {decides && !canWrite && <NotPermitted id={notPermitted} what="reinstating an advisor" className="text-xs" />}
      {gatherError && <LoadError message={gatherError} />}
      {decides && (
        <SelectionBar
          count={tickedRows.length}
          onClear={() => setTicked(new Set())}
          stickyTop={STICKY_BAR_TOP}
        >
          <Button
            {...gated}
            disabled={!canWrite}
            onClick={() => setAsking({ decision: 'reinstate', rows: tickedRows })}
          >
            Reinstate
          </Button>
          <Button
            {...gated}
            variant="outline"
            disabled={!canWrite}
            onClick={() => setAsking({ decision: 'keep_out', rows: tickedRows })}
          >
            Keep out
          </Button>
          {handling === 'bulk_except_noted' &&
            (
              [
                [true, 'Hold back'],
                [false, 'Release']
              ] as const
            ).map(([hold, text]) => (
              <Button key={text} type="button" size="sm" variant="ghost" onClick={() => move(hold)}>
                {text}
              </Button>
            ))}
        </SelectionBar>
      )}
      <DataTable
        label={label}
        columns={columns}
        rows={rows}
        rowKey={rowKeyOf}
        rowProps={(row) =>
          (exceptions.has(rowKeyOf(row)) ? { 'data-exception': '' } : {}) as React.HTMLAttributes<HTMLTableRowElement>
        }
        state={tableState(
          read,
          signalled ? 'Nobody under this reason matches the signals pressed.' : 'Nobody under this reason now.',
          rows?.length ?? 0
        )}
        busy={read.reading && rows !== null}
        selectable={selection}
        grouping={exceptions.size > 0 ? grouping : undefined}
        stickyTop={STICKY_TABLE_TOP}
      />
      <WorkPager {...paging.pager} total={read.data?.total ?? count} busy={read.reading} sticky />
      <DecideDialog
        survey={survey}
        reason={label}
        asking={asking}
        quality={fields.quality.code}
        onClose={() => setAsking(null)}
        onDone={(decision, result, failure) => {
          onDecided(sayResult(decision, result, label) + (failure ? ` The rest were not sent: ${failure}` : ''));
          void read.reload();
        }}
      />
    </WorklistSection>
  );
});

function sayResult(decision: ReinstatementDecision, result: IReinstatementResult, reason: string): string {
  return `${[
    `${reason}: ${result.applied.length} ${decision === 'reinstate' ? 'reinstated' : 'kept out'}`,
    result.conflicts.length > 0 &&
      `${result.conflicts.length} changed by a colleague since you read them and left as they are`,
    result.skipped.length > 0 && `${result.skipped.length} no longer on the worklist`
  ]
    .filter(Boolean)
    .join('; ')}.`;
}

/** The decision's confirmation, with an optional note survey-api keeps on the batch. */
function DecideDialog({
  survey,
  reason,
  asking,
  quality,
  onClose,
  onDone
}: {
  survey: string;
  reason: string;
  asking: Nullable<{ decision: ReinstatementDecision; rows: IListRow[] }>;
  quality: string;
  onClose: () => void;
  /** `failure` where a later batch failed after earlier ones were applied. */
  onDone: (decision: ReinstatementDecision, result: IReinstatementResult, failure?: string) => void;
}) {
  const decision = asking?.decision ?? 'reinstate';
  const rows = asking?.rows ?? [];
  const verb = decision === 'reinstate' ? 'Reinstate' : 'Keep out';
  return (
    <ReasonDialog
      open={asking !== null}
      onOpenChange={(open) => !open && onClose()}
      title={`${verb} ${countOf(rows.length, 'advisor')}`}
      description={
        decision === 'reinstate'
          ? `Travel becomes 1 and “${reason}” gives way to the advisor’s last score, or is cleared where there is none.`
          : `“${reason}” stays, and the advisor leaves the worklist until the next cycle opens.`
      }
      reasonLabel="Note"
      required={false}
      max={2000}
      confirmLabel={verb}
      onConfirm={async (note) => {
        // survey-api takes 2,000 rows a request; a larger group goes as several batches, and one
        // that fails after others were applied is said beside what they did.
        const result: IReinstatementResult = { batchId: '', applied: [], conflicts: [], skipped: [] };
        let at = 0;
        try {
          for (; at < rows.length; at += REINSTATE_BATCH) {
            const part = await listsApi.reinstate({
              survey,
              decision,
              ...(note ? { note } : {}),
              participants: rows.slice(at, at + REINSTATE_BATCH).map((row) => ({
                participantId: row.participantId,
                version: row.cells[quality]?.version ?? 0
              }))
            });
            result.batchId = part.batchId;
            result.applied.push(...part.applied);
            result.conflicts.push(...part.conflicts);
            result.skipped.push(...part.skipped);
          }
          onDone(decision, result);
          return null;
        } catch (error) {
          const failure = refusalMessage(error, 'Could not save the decision');
          if (at === 0) return failure;
          onDone(decision, result, failure);
          return null;
        }
      }}
    />
  );
}

/** Legacy Travel 2 and 3 (WF-08 9): reviewed against the year's thresholds, and Travel set in Lists. */
function LegacyTravelSection({
  survey,
  fields,
  byCode,
  count,
  own,
  filters,
  page,
  slot,
  barSlot,
  reread,
  signalled
}: {
  survey: string;
  fields: IWorklistFields;
  byCode: ReadonlyMap<string, IListField>;
  count: number;
  own: IListCriteriaGroup['children'];
  filters: IListCriteriaGroup['children'];
  page: IWorkPage;
  slot: Nullable<HTMLElement>;
  barSlot: Nullable<HTMLElement>;
  reread: number;
  /** Whether a signal is pressed, which an empty table says. */
  signalled: boolean;
}) {
  const codes = [NAME_FIELD, FIRM, IDENTIFIER, fields.travel.code, fields.quality.code, LAST_EDIT];
  const { sorts } = page;
  const scope: IWorkScope = { query: { survey }, base: legacyTravel(fields, ...own) };
  const paging = useWorkPaging({ tab: LEGACY_TAB, replace: true });
  const chosen = useWorkColumns(page, `reinstatement.${survey}.${LEGACY_TAB}`, codes);
  const read = useAdminData(
    (signal) =>
      readSection(
        {
          survey,
          criteria: legacyTravel(fields, ...filters),
          columns: [...codes, ...chosen.extras],
          sorts: sorts.length > 0 ? [...sorts, BY_NAME] : [{ field: LAST_EDIT, dir: 'desc' }]
        },
        paging,
        signal
      ),
    [survey, filters, sorts, chosen.extras.join(','), paging.page, paging.perPage, paging.pages, reread]
  );
  const fixedColumns: IDataColumn<IListRow>[] = codes.map((code) => ({
    id: code,
    label: byCode.get(code)?.label ?? code,
    header: <WorkHeader page={page} scope={scope} code={code} />,
    cell: (row: IListRow) => {
      const field = byCode.get(code);
      const value = field ? cellText(field, row.cells[code]) : '';
      return code === NAME_FIELD ? <RecordLink row={row} name={value} /> : value;
    }
  }));
  const columns = chosen.resolve(fixedColumns, scope);
  const rows = read.data?.rows ?? null;
  return (
    <WorklistSection reason="legacy-travel" title="Legacy Travel 2 or 3">
      <SectionControls slot={slot} barSlot={barSlot}>
        <HelpPopover label="About Legacy Travel 2 or 3" align="end">
          <p>
            Where the advisor has edited recently, check AUM, production and quality against this year’s thresholds,
            and set Travel in Lists.
          </p>
        </HelpPopover>
        {chosen.button(fixedColumns)}
      </SectionControls>
      <DataTable
        label="Legacy Travel 2 or 3"
        columns={columns}
        rows={rows}
        rowKey={rowKeyOf}
        state={tableState(
          read,
          signalled
            ? 'Nobody on legacy Travel 2 or 3 matches the signals pressed.'
            : 'Nobody on legacy Travel 2 or 3 now.',
          rows?.length ?? 0
        )}
        busy={read.reading && rows !== null}
        stickyTop={STICKY_TABLE_TOP}
      />
      <WorkPager {...paging.pager} total={read.data?.total ?? count} busy={read.reading} sticky />
    </WorklistSection>
  );
}

/** A section's own controls, at the toolbar's end — and again, condensed, in the compact bar while
 *  it shows. */
function SectionControls({
  slot,
  barSlot,
  children
}: {
  slot: Nullable<HTMLElement>;
  barSlot: Nullable<HTMLElement>;
  children: React.ReactNode;
}) {
  return (
    <>
      {slot && createPortal(children, slot)}
      <CompactPortal to={barSlot}>{children}</CompactPortal>
    </>
  );
}

/** A row's name, a link to the advisor's record page; Back returns to the worklist as it was. */
function RecordLink({ row, name }: { row: IListRow; name: string }) {
  return <ParticipantLink participant={{ participantId: row.participantId, displayName: name || 'Unnamed' }} />;
}

/** A column the picker offers, off until asked for — unless `on`, when it is simply drawn. */
function offUnless(on: boolean): Partial<IDataColumn<IListRow>> {
  return on ? {} : { optional: true, hidden: true };
}
